function mapById(items = [], keySelector) {
  return items.reduce((accumulator, item) => {
    const key = keySelector(item);

    if (key == null || key === "") {
      return accumulator;
    }

    if (!accumulator.has(String(key))) {
      accumulator.set(String(key), item);
    }

    return accumulator;
  }, new Map());
}

function toDateKey(value) {
  return String(value || "").slice(0, 10);
}

function toTimeLabel(value) {
  const text = String(value || "");
  const match = text.match(/T(\d{2}:\d{2})|(\d{2}:\d{2})/);

  if (!match) {
    return "";
  }

  return match[1] || match[2] || "";
}

function buildCallSummary(call = {}) {
  if (call.summary) {
    return sanitizeDisplayText(call.summary);
  }

  if (call.disposition) {
    return `Disposition: ${call.disposition}`;
  }

  if (call.status) {
    return `Call ${String(call.status).replace(/_/g, " ")}`;
  }

  return "";
}

function sanitizeDisplayText(value) {
  return String(value || "")
    .replace(/\b(?:media_)?worker_stopped\b/gi, "system ended the call")
    .replace(/\bmedia_ended\b/gi, "media ended")
    .replace(/\s+/g, " ")
    .trim();
}

function buildLeadTask(call = {}, schedule = {}) {
  const normalizedDisposition = String(call.disposition || "").toLowerCase();
  const normalizedScheduleStatus = String(
    schedule.schedule_status || schedule.status || "",
  ).toLowerCase();

  if (normalizedDisposition.includes("book")) {
    return "confirm_appointment";
  }

  if (normalizedScheduleStatus === "pending" || normalizedScheduleStatus === "queued") {
    return "call_back_at_time";
  }

  if (Number(call.lead_score || 0) >= 70) {
    return "interested";
  }

  return "follow_up";
}

function buildNextBestAction(call = {}, schedule = {}) {
  const task = buildLeadTask(call, schedule);

  if (task === "confirm_appointment") {
    return "Confirm appointment";
  }

  if (task === "call_back_at_time") {
    return "Call at scheduled time";
  }

  if (task === "interested") {
    return "Warm follow-up";
  }

  return "General follow-up";
}

export function mapHistoryCallToDashboardCall(call = {}) {
  const transcriptionCount = Number(
    call.transcription_count || call.transcript_count || 0,
  );
  const transcript = call.transcript || "";
  const metadata = call.metadata || {};
  const lineLabel =
    call.line_label ||
    metadata.caller_id ||
    metadata.trunk ||
    metadata.line ||
    "";

  return {
    id: String(call.call_id || ""),
    call_id: call.call_id,
    created_at: call.ended_at || call.started_at || call.created_at || null,
    direction: call.direction || call.call_direction || "outbound",
    call_direction: call.direction || call.call_direction || "outbound",
    phone_number: call.phone_number || call.phone || call.contact?.phone || "",
    caller_name:
      call.caller_name ||
      call.patient_name ||
      call.name ||
      call.contact_name ||
      call.contact?.name ||
      "",
    duration_seconds: Number(
      call.duration_seconds ??
        call.latest_attempt_duration_seconds ??
        call.latest_attempt?.duration_seconds ??
        0,
    ),
    summary: buildCallSummary(call),
    status: call.status || "",
    booking_status:
      /book|convert|qualified|won|successful/i.test(String(call.disposition || ""))
        ? "booked"
        : Number(call.duration_seconds || 0) > 0 && Number(call.duration_seconds || 0) < 10
          ? "dropped"
          : "info",
    service_type: call.service_type || "",
    call_disposition: call.disposition || call.call_disposition || "",
    transcript,
    transcription_count: transcriptionCount,
    has_transcript: Boolean(call.has_transcript) || transcriptionCount > 0 || Boolean(transcript),
    recording_count: Number(call.recording_count || 0),
    recording_status: call.recording_status || "",
    recording_s3_key: call.recording_s3_key || "",
    metadata,
    line_label: lineLabel,
  };
}

export function filterDashboardCalls(calls = [], filters = {}) {
  const search = String(filters.search || "").trim().toLowerCase();
  const status = String(filters.status || "").trim().toLowerCase();
  const direction = String(filters.direction || "").trim().toLowerCase();
  const dateFrom = String(filters.date_from || "").slice(0, 10);
  const dateTo = String(filters.date_to || "").slice(0, 10);
  const line = String(filters.line || "").trim().toLowerCase();
  const hasMinDuration = filters.duration_min !== undefined && filters.duration_min !== null && filters.duration_min !== "";
  const hasMaxDuration = filters.duration_max !== undefined && filters.duration_max !== null && filters.duration_max !== "";
  const minDuration = Number(filters.duration_min);
  const maxDuration = Number(filters.duration_max);

  return calls.filter((call) => {
    if (status && String(call.booking_status || "").toLowerCase() !== status) {
      return false;
    }

    if (direction && String(call.call_direction || call.direction || "").toLowerCase() !== direction) {
      return false;
    }

    const callDate = String(call.created_at || call.started_at || "").slice(0, 10);
    if (dateFrom && callDate && callDate < dateFrom) {
      return false;
    }
    if (dateTo && callDate && callDate > dateTo) {
      return false;
    }

    const duration = Number(call.duration_seconds || 0);
    if (hasMinDuration && duration < minDuration) {
      return false;
    }
    if (hasMaxDuration && duration >= maxDuration) {
      return false;
    }

    if (line) {
      const metadata = call.metadata || {};
      const lineHaystack = [
        call.phone_number,
        call.line_label,
        metadata.caller_id,
        metadata.trunk,
        metadata.line,
        metadata.phone,
        metadata.phone_number,
      ]
        .join(" ")
        .toLowerCase();

      if (!lineHaystack.includes(line)) {
        return false;
      }
    }

    if (!search) {
      return true;
    }

    const haystack = [
      call.phone_number,
      call.caller_name,
      call.summary,
      call.status,
      call.line_label,
    ]
      .join(" ")
      .toLowerCase();

    return haystack.includes(search);
  });
}

export function buildDashboardStats(callHistory = [], schedules = []) {
  const totalCalls = Number(callHistory.length || 0);
  const completedCalls = callHistory.filter(
    (call) => String(call.status || "").toLowerCase() === "completed",
  ).length;
  const failedCalls = callHistory.filter((call) => {
    const normalizedStatus = String(call.status || "").toLowerCase();
    return normalizedStatus === "failed" || normalizedStatus === "cancelled";
  }).length;
  const droppedCalls = callHistory.filter(
    (call) => Number(call.duration_seconds || 0) > 0 && Number(call.duration_seconds || 0) < 10,
  ).length;
  const inboundCalls = callHistory.filter(
    (call) => String(call.direction || "").toLowerCase() === "inbound",
  ).length;
  const outboundCalls = totalCalls - inboundCalls;
  const avgDuration =
    totalCalls > 0
      ? Number(
          (
            callHistory.reduce(
              (total, call) => total + Number(call.duration_seconds || 0),
              0,
            ) / totalCalls
          ).toFixed(2),
        )
      : 0;
  const totalBookings = schedules.filter((schedule) => {
    const normalizedStatus = String(schedule.status || schedule.schedule_status || "")
      .toLowerCase();
    return normalizedStatus === "completed";
  }).length;
  const totalBookingRequests = schedules.filter((schedule) => {
    const normalizedStatus = String(schedule.status || schedule.schedule_status || "")
      .toLowerCase();
    return ["pending", "queued", "scheduled", "in_progress"].includes(normalizedStatus);
  }).length;
  const bookingRate = totalCalls > 0 ? Number(((totalBookings / totalCalls) * 100).toFixed(2)) : 0;

  return {
    total_calls: totalCalls,
    completed_calls: completedCalls,
    failed_calls: failedCalls,
    dropped_calls: droppedCalls,
    total_bookings: totalBookings,
    total_booking_requests: totalBookingRequests,
    avg_duration: avgDuration,
    booking_rate: bookingRate,
    inbound_calls: inboundCalls,
    outbound_calls: outboundCalls,
  };
}

export function buildLeadRecords(contacts = [], callHistory = [], scheduledCalls = []) {
  const latestCallsByContact = mapById(callHistory, (call) => call.contact_id);
  const latestSchedulesByContact = mapById(scheduledCalls, (call) => call.contact_id);

  return contacts.map((contact) => {
    const latestCall = latestCallsByContact.get(String(contact.contact_id)) || {};
    const latestSchedule =
      latestSchedulesByContact.get(String(contact.contact_id)) || {};
    const fullName = String(
      contact.name ||
        [contact.first_name, contact.last_name].filter(Boolean).join(" ").trim() ||
        latestCall.contact?.name ||
        "",
    ).trim();
    const preferredTime = toTimeLabel(latestSchedule.scheduled_time);

    return {
      id: String(contact.contact_id),
      contact_id: contact.contact_id,
      full_name: fullName || "No name",
      phone: contact.phone || contact.phone_number || "",
      email: contact.email || "",
      crm_id: contact.crm_id || "",
      lead_score: Number(latestCall.lead_score || 0),
      pam_task: buildLeadTask(latestCall, latestSchedule),
      next_best_action: buildNextBestAction(latestCall, latestSchedule),
      summary_text: buildCallSummary(latestCall),
      service_interest: latestSchedule.campaign_name || "General Consultation",
      preferred_time: preferredTime || "--",
      status: contact.status || latestSchedule.schedule_status || "active",
      created_at: contact.created_at || null,
    };
  });
}

export function buildLeadContextStatus(contacts = []) {
  return {
    enabled: true,
    lead_count: contacts.length,
    source: contacts.length ? "contacts" : "empty",
    last_synced_at: contacts[0]?.created_at || null,
  };
}

export function mapScheduledCallToAppointment(schedule = {}) {
  const dateText = toDateKey(schedule.scheduled_time);
  const timeText = toTimeLabel(schedule.scheduled_time);
  const normalizedStatus = String(
    schedule.schedule_status || schedule.status || "",
  ).toLowerCase();
  let appointmentStatus = "requested";

  if (normalizedStatus === "completed") {
    appointmentStatus = "booked";
  } else if (["cancelled", "failed"].includes(normalizedStatus)) {
    appointmentStatus = "cancelled";
  }

  return {
    id: String(schedule.schedule_id || ""),
    schedule_id: schedule.schedule_id,
    patient_id: schedule.contact_id,
    patient_name: schedule.contact?.name || "Patient",
    title: schedule.contact?.name || "Patient",
    phone: schedule.contact?.phone || "",
    email: schedule.contact?.email || "",
    date: dateText,
    time: timeText,
    status: appointmentStatus,
    service_type: schedule.campaign_name || "clinic",
    service_name: schedule.campaign_name || "Consultation",
    notes: schedule.call_status
      ? `Latest call status: ${String(schedule.call_status).replace(/_/g, " ")}`
      : "",
    doctor_name: "",
    booking_source: schedule.direction || "dashboard",
    request_id: schedule.schedule_id,
    confirmation_number:
      appointmentStatus === "booked" ? `SCH-${schedule.schedule_id}` : "",
  };
}

export function filterAppointmentsByStatus(appointments = [], statusFilter = "all") {
  if (!statusFilter || statusFilter === "all") {
    return appointments;
  }

  return appointments.filter(
    (appointment) => String(appointment.status || "").toLowerCase() === statusFilter,
  );
}

export function buildAvailableSlots(dateText, scheduledCalls = []) {
  const busySlots = new Set(
    scheduledCalls
      .filter((schedule) => toDateKey(schedule.scheduled_time) === dateText)
      .map((schedule) => toTimeLabel(schedule.scheduled_time))
      .filter(Boolean),
  );
  const slots = [];

  for (let hour = 10; hour <= 19; hour += 1) {
    ["00", "30"].forEach((minute) => {
      const slot = `${String(hour).padStart(2, "0")}:${minute}`;

      if (!busySlots.has(slot)) {
        slots.push(slot);
      }
    });
  }

  return slots;
}

export function buildPerformanceData(calls = []) {
  const days = new Map();
  const today = new Date();

  for (let index = 6; index >= 0; index -= 1) {
    const day = new Date(today);
    day.setDate(day.getDate() - index);
    const key = day.toISOString().slice(0, 10);

    days.set(key, {
      label: String(day.getDate()),
      incoming: 0,
      answer: 0,
      shadow: 0,
    });
  }

  calls.forEach((call) => {
    const key = toDateKey(call.created_at || call.started_at);

    if (!days.has(key)) {
      return;
    }

    const day = days.get(key);
    day.incoming += 1;

    if (String(call.status || "").toLowerCase() === "completed") {
      day.answer += 1;
    }

    day.shadow = Math.round((day.incoming + day.answer) / 2);
  });

  const chartData = Array.from(days.values());
  const answeredCalls = calls.filter(
    (call) => String(call.status || "").toLowerCase() === "completed",
  ).length;
  const avgDurationSeconds =
    calls.length > 0
      ? Math.round(
          calls.reduce(
            (total, call) => total + Number(call.duration_seconds || 0),
            0,
          ) / calls.length,
        )
      : 0;
  const avgWaitSeconds = calls.length
    ? Math.max(
        0,
        Math.round(
          calls.reduce((total, call) => {
            if (!call.started_at || !call.created_at) {
              return total;
            }

            return (
              total +
              Math.max(
                0,
                Math.round(
                  (new Date(call.started_at).getTime() -
                    new Date(call.created_at).getTime()) /
                    1000,
                ),
              )
            );
          }, 0) / calls.length,
        ),
      )
    : 0;
  const answerRate = calls.length
    ? `${((answeredCalls / calls.length) * 100).toFixed(2)}%`
    : "0%";

  return {
    chartData,
    metrics: [
      {
        key: "talk-time",
        label: "AVG TALK TIME",
        value: avgDurationSeconds ? `${avgDurationSeconds}s` : "--",
      },
      {
        key: "wait-time",
        label: "AVG WAIT TIME",
        value: avgWaitSeconds ? `${avgWaitSeconds}s` : "--",
      },
      {
        key: "answered-calls",
        label: "ANSWERED CALLS",
        value: answerRate,
      },
    ],
  };
}
